import { differenceInDays, isFuture, isToday } from 'date-fns';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';

import * as Models from 'contracts/models';
import IssueCard from 'pages/components/AuthWidget/components/IssueCard';
import formatDate from 'utils/format-date';
import parseDate from 'utils/parse-date';

interface Props {
    license: Models.UserInfo.License;
    onClick?: () => void;
    className?: string;
}

const LicenseIssue: React.FC<Props> = ({ license, onClick, className }) => {
    const history = useHistory();

    const goToContacts = useCallback(() => {
        history.push('/company/contacts');
        onClick?.();
    }, [history, onClick]);

    const expiresAt = parseDate(license.licenseExpiration);

    const isTrial = /trial/i.test(license.licenseType);
    const isOutOfLimit = license.amount <= 0;
    const isCloseToOutOfLimit = license.amount / license.capacity <= 0.1;
    const isLocked = license.status === Models.UserInfo.LicenseStatus.LOCKED;
    const isExpired = license.status === Models.UserInfo.LicenseStatus.EXPIRED;
    const isExpireToday = isToday(expiresAt);
    const isCloseToExpire = isFuture(expiresAt) && differenceInDays(expiresAt, Date.now()) <= 7;

    if (isLocked) {
        return (
            <IssueCard
                variant="danger"
                title={'Your plan is locked!'}
                description={'Contact support for more details'}
                action={'Contacts'}
                onClick={goToContacts}
                className={className}
            />
        );
    }

    if (isExpired) {
        return (
            <IssueCard
                variant="danger"
                title={'Your subscription ended'}
                description={'Upgrade your plan to continue enjoying our services!'}
                action={'Upgrade'}
                onClick={goToContacts}
                className={className}
            />
        );
    }

    if (isExpireToday) {
        return (
            <IssueCard
                variant="danger"
                title={'Your subscription ends today'}
                description={'Pay now to continue using!'}
                action={'Pay now'}
                onClick={goToContacts}
                className={className}
            />
        );
    }

    if (isOutOfLimit) {
        return isTrial ? (
            <IssueCard
                variant="danger"
                title={'30-day trial has ended'}
                description={'Upgrade your plan to continue enjoying our services!'}
                action={'Upgrade'}
                onClick={goToContacts}
                className={className}
            />
        ) : (
            <IssueCard
                variant="danger"
                title={"You're out of credit"}
                description={'Upgrade your plan to get unlimited requests!'}
                action={'Upgrade'}
                onClick={goToContacts}
                className={className}
            />
        );
    }

    if (isCloseToOutOfLimit) {
        return isTrial ? (
            <IssueCard
                variant="warning"
                title={'Your trial plan is about to expire'}
                description={'Upgrade your plan to continue enjoying our services!'}
                action={'Upgrade'}
                onClick={goToContacts}
                className={className}
            />
        ) : (
            <IssueCard
                variant="warning"
                title={'You are nearing your request limit'}
                description={'Upgrade your plan to get unlimited requests!'}
                action={'Upgrade'}
                onClick={goToContacts}
                className={className}
            />
        );
    }

    if (isCloseToExpire) {
        return (
            <IssueCard
                variant="warning"
                title={
                    <>
                        {'Your subscription ends on'}
                        &nbsp;
                        <strong>{formatDate(expiresAt, 'MMMM d, yyyy')}</strong>
                    </>
                }
                action={'Upgrade'}
                onClick={goToContacts}
                className={className}
            />
        );
    }

    return null;
};

export default LicenseIssue;
